import { Tooltip, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { CURRENCIES } from "currency/model"
import { getValueInUserCurrency } from "currency/tools/common"
import { getEnvironmentById } from "environments/tools/common"
import { getCurrencySymbol, getEcpm } from "form/inventory/tools/common"
import React from "react"
import { IScreenData, IScreenDataById } from "screen/model"
import { getScreensByIds } from "screen/tools/common"
import { LinearLoader } from "ui/components/LinearLoader"
import { StreetViewButton } from "ui/components/StreetViewButton"
import { getDataGridTexts } from "ui/tools/list"
import { getUserCurrencyId } from "user/tools/common"

interface IScreenDetailTableProps {
  screen_ids: string[]
  screen_impressions: number[]
  screen_spends: number[]
}

const HeaderName = ({ name }: { name: string }) => (
  <Tooltip title={name} PopperProps={{ style: { zIndex: 14448888880 } }}>
    <Typography
      color="textPrimary"
      fontSize=".875rem"
      fontWeight={500}
      sx={{
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap"
      }}>
      {name}
    </Typography>
  </Tooltip>
)

export const ScreenDetailTable = React.memo(
  ({ screen_ids, screen_impressions, screen_spends }: IScreenDetailTableProps) => {
    const [screenDataById, setScreenDataById] = React.useState<null | IScreenDataById>(null)
    const [hasError, setHasError] = React.useState(false)
    if (typeof screen_ids === "string") {
      screen_ids = JSON.parse(screen_ids)
    }
    if (typeof screen_impressions === "string") {
      screen_impressions = JSON.parse(screen_impressions)
    }
    if (typeof screen_spends === "string") {
      screen_spends = JSON.parse(screen_spends)
    }
    React.useEffect(() => {
      getScreensByIds(screen_ids as string[])
        .then(fetchedScreens => {
          const newScreenById: IScreenDataById = {}
          for (let i = 0; i < screen_ids.length; i++) {
            const screenId = screen_ids[i]
            const fetchedScreen = fetchedScreens.find(s => s.device_id === screenId)
            if (!fetchedScreen) {
              setHasError(true)
              return
            }
            newScreenById[fetchedScreen.device_id] = {
              screen: fetchedScreen,
              impressions: screen_impressions[i],
              spend: screen_spends[i]
            }
          }
          setScreenDataById(newScreenById)
        })
        .catch(error => {
          console.error(error)
          setHasError(true)
        })
    }, [])
    if (hasError) {
      return (
        <Typography color="error" fontSize=".9rem">
          {translate("label.error.unexpected")}
        </Typography>
      )
    } else if (screenDataById === null) {
      return <LinearLoader sx={{ mt: "1rem" }} />
    }
    const columns: Array<GridColDef<IScreenData>> = [
      {
        field: "name",
        renderHeader: () => <HeaderName name={translate("label.screen")} />,
        flex: 2,
        sortable: false,
        renderCell: params => (
          <Tooltip
            title={`${translate("label.name")}: ${params.row.screen.screen_name} ; ID: ${params.row.screen.device_id}`}
            PopperProps={{ style: { zIndex: 14448888880 } }}>
            <Typography color="textPrimary" fontSize=".75rem">
              {params.row.screen.screen_name}
            </Typography>
          </Tooltip>
        )
      },
      {
        field: "impressions",
        renderHeader: () => <HeaderName name={translate("label.impressions")} />,
        flex: 1,
        sortable: false
      },
      {
        field: "ecpm",
        renderHeader: () => <HeaderName name={translate("label.ecpm.average")} />,
        sortable: false,
        flex: 1,
        renderCell: params =>
          `${getEcpm(getValueInUserCurrency(CURRENCIES.CAD, params.row.spend), params.row.impressions)} ${getCurrencySymbol(getUserCurrencyId())}`
      },
      {
        field: "venue-type",
        renderHeader: () => <HeaderName name={translate("label.venue-type")} />,
        flex: 2,
        sortable: false,
        renderCell: params => {
          let venueTypes: string[] = []
          if (params.row.screen.venue_type_ids) {
            // ex: "|46|34|09|"
            venueTypes = params.row.screen.venue_type_ids
              .split("|")
              .filter(vt => vt !== "")
              .map(id => getEnvironmentById(Number(id)).name)
          }
          const text = venueTypes.join(", ")
          return (
            <Tooltip title={text} PopperProps={{ style: { zIndex: 14448888880 } }}>
              <Typography color="textPrimary" fontSize=".75rem">
                {text}
              </Typography>
            </Tooltip>
          )
        }
      },
      {
        field: "actions",
        renderHeader: () => <HeaderName name={translate("label.actions")} />,
        flex: 1,
        sortable: false,
        renderCell: params => (
          <StreetViewButton latitude={params.row.screen.latitude} longitude={params.row.screen.longitude} />
        )
      }
    ]
    return (
      <DataGrid
        rows={screen_ids.map(screenId => screenDataById[screenId])}
        columns={columns}
        getRowId={row => row.screen.device_id}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        className="map-screens-list"
        rowHeight={30}
        headerHeight={30}
        pageSize={5}
        localeText={getDataGridTexts()}
        sx={{
          "width": "30rem",
          "minHeight": "14rem",
          "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTablePagination-toolbar": {
            minHeight: "1rem",
            height: "2rem"
          },
          "& .MuiTablePagination-root": {
            overflow: "hidden"
          },
          "& .MuiDataGrid-cell": {
            fontSize: ".75rem"
          }
        }}
      />
    )
  }
)
